import React, { useState, useEffect } from 'react';
import db from '../../utils/database';

const TableListManager = ({ password, onRefreshTables }) => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateTable, setShowCreateTable] = useState(false);
  const [newTableName, setNewTableName] = useState('');
  const [tableToDelete, setTableToDelete] = useState(null);
  const [newTableColumns, setNewTableColumns] = useState([
    { name: 'id', type: 'INTEGER', primaryKey: true, autoIncrement: true, notNull: true }
  ]);
  
  // Load all tables when component mounts
  useEffect(() => {
    loadTables();
  }, []);
  
  // Load all available tables
  const loadTables = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const tableList = await db.getTables();
      setTables(tableList);
    } catch (err) {
      console.error("Failed to load tables:", err);
      setError(`Failed to load tables: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Create a new table
  const createNewTable = async () => {
    if (!newTableName.trim()) {
      setError('Table name is required');
      return;
    }
    
    if (newTableColumns.length === 0) {
      setError('At least one column is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Build CREATE TABLE query
      let query = `CREATE TABLE "${newTableName}" (\n`;
      
      // Add columns
      const columnDefs = newTableColumns.map(column => {
        let columnDef = `  "${column.name}" ${column.type}`;
        
        if (column.notNull) {
          columnDef += ' NOT NULL';
        }
        
        if (column.primaryKey) {
          columnDef += ' PRIMARY KEY';
          
          if (column.autoIncrement) {
            columnDef += ' AUTOINCREMENT';
          }
        }
        
        return columnDef;
      });
      
      query += columnDefs.join(',\n');
      query += '\n);';
      
      // Execute the query
      await db.query(query);
      
      // Reset form
      setNewTableName('');
      setNewTableColumns([
        { name: 'id', type: 'INTEGER', primaryKey: true, autoIncrement: true, notNull: true }
      ]);
      setShowCreateTable(false);
      
      // Refresh the table list
      await loadTables();
      
      // Notify parent to update table list
      if (onRefreshTables) {
        onRefreshTables();
      }
      
    } catch (err) {
      setError(`Failed to create table: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a table
  const deleteTable = async (tableName) => {
    if (!tableName) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Execute DROP TABLE query
      await db.query(`DROP TABLE "${tableName}"`);
      
      // Reset state
      setTableToDelete(null);
      
      // Refresh the table list
      await loadTables();
      
      // Notify parent to update table list
      if (onRefreshTables) {
        onRefreshTables();
      }
      
    } catch (err) {
      setError(`Failed to delete table: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Add a column to the new table definition
  const addColumn = () => {
    setNewTableColumns([
      ...newTableColumns,
      { name: '', type: 'TEXT', primaryKey: false, autoIncrement: false, notNull: false }
    ]);
  };
  
  // Remove a column from the new table definition
  const removeColumn = (index) => {
    const updatedColumns = [...newTableColumns];
    updatedColumns.splice(index, 1);
    setNewTableColumns(updatedColumns);
  };
  
  // Update a column definition
  const updateColumn = (index, field, value) => {
    const updatedColumns = [...newTableColumns];
    updatedColumns[index] = {
      ...updatedColumns[index],
      [field]: field === 'notNull' || field === 'primaryKey' || field === 'autoIncrement' ? 
        value === true || value === 'true' : value
    };
    setNewTableColumns(updatedColumns);
  };
  
  // Render the create table form
  const renderCreateTableForm = () => {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Create New Table</h3>
          
          <div className="mt-5 space-y-6">
            <div>
              <label htmlFor="tableName" className="block text-sm font-medium text-gray-700">
                Table Name
              </label>
              <input
                type="text"
                id="tableName"
                value={newTableName}
                onChange={(e) => setNewTableName(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">
                  Columns
                </label>
                <button
                  type="button"
                  onClick={addColumn}
                  className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
              </div>
              
              <div className="mt-2 space-y-2">
                {newTableColumns.map((column, index) => (
                  <div key={index} className="flex flex-wrap items-center gap-4 p-3 border rounded-md bg-gray-50">
                    <div className="w-full sm:w-auto">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={column.name}
                        onChange={(e) => updateColumn(index, 'name', e.target.value)}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="w-full sm:w-auto">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Type
                      </label>
                      <select
                        value={column.type}
                        onChange={(e) => updateColumn(index, 'type', e.target.value)}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="INTEGER">INTEGER</option>
                        <option value="TEXT">TEXT</option>
                        <option value="REAL">REAL</option>
                        <option value="BLOB">BLOB</option>
                        <option value="NUMERIC">NUMERIC</option>
                        <option value="BOOLEAN">BOOLEAN</option>
                        <option value="DATE">DATE</option>
                        <option value="DATETIME">DATETIME</option>
                        <option value="TIMESTAMP">TIMESTAMP</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={column.notNull}
                          onChange={(e) => updateColumn(index, 'notNull', e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-xs text-gray-700">NOT NULL</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={column.primaryKey}
                          onChange={(e) => updateColumn(index, 'primaryKey', e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-xs text-gray-700">Primary Key</span>
                      </label>
                      
                      {column.primaryKey && column.type === 'INTEGER' && (
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={column.autoIncrement}
                            onChange={(e) => updateColumn(index, 'autoIncrement', e.target.checked)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-xs text-gray-700">Auto Increment</span>
                        </label>
                      )}
                    </div>
                    
                    {newTableColumns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeColumn(index)}
                        className="ml-auto p-1 rounded-full text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowCreateTable(false)}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={createNewTable}
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Table'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Render delete confirmation dialog
  const renderDeleteConfirmation = () => {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-4">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Delete Table
          </h3>
          
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>
              Are you sure you want to delete the table "{tableToDelete}"? This action cannot be undone.
            </p>
            <div className="mt-2 bg-red-50 p-3 rounded-md">
              <p className="text-sm font-semibold text-red-800">
                All data in this table will be permanently lost.
              </p>
            </div>
          </div>
          
          <div className="mt-5 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setTableToDelete(null)}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => deleteTable(tableToDelete)}
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              {loading ? 'Deleting...' : 'Delete Table'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading && tables.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
        <strong className="font-bold">Error:</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }
  
  if (showCreateTable) {
    return renderCreateTableForm();
  }
  
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Table Management
          </h3>
          
          <button
            onClick={() => setShowCreateTable(true)}
            className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <svg className="inline-block h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Create New Table
          </button>
        </div>
      </div>
      
      {tableToDelete && renderDeleteConfirmation()}
      
      <div className="overflow-hidden">
        {tables.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No tables found in the database.</p>
            <p className="text-sm text-gray-400 mt-2">Create a new table to get started.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {tables.map((table) => (
              <li key={table} className="px-4 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium text-gray-900">{table}</span>
                </div>
                
                <div className="flex items-center">
                  <button
                    onClick={() => setTableToDelete(table)}
                    className="ml-4 text-red-600 hover:text-red-900 focus:outline-none"
                    title="Delete Table"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TableListManager;